import { cx } from '@emotion/css';
import { ContactsTwoTone } from '@mui/icons-material';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import { isNonEmptyString } from '@sortlist-frontend/utils';
import { Fragment } from 'react';

import { MediaAttachmentFactory } from '_backend/queries/shared/media-attachment';
import { ClampLines } from '_components/common/ClampLines';
import { CtaBox } from '_components/common/CtaBox';
import { LazyImage } from '_components/image/lazy-image';
import { usePublicAppContext } from '_core/context/public-app-context';
import { getGumletImg, onImageError } from '_core/media/get-media.utils';
import { SectionBox } from '_features/AgencyProfile/components/SectionBox';
import { agencyProfileConfig } from '_features/AgencyProfile/config';
import { useGetAgencyProfile } from '_features/AgencyProfile/hooks/useGetAgencyProfile';

import * as S from './styles';

interface Props {
  className?: string;
  children?: never;
}

export const TeamPanel = (props: Props) => {
  const { className } = props;
  const { t } = useTranslation(agencyProfileConfig.i18nNamespaces);
  const { locale, pageData } = usePublicAppContext();
  const { agencySlug } = pageData as { agencySlug: string };
  const { data: { agency } = {} } = useGetAgencyProfile({ agencySlug, locale: locale as string });

  const agencyName = agency?.name;
  const memberCount = agency?.teamSize ?? 0;
  const { story, teamPictureUrl } = agency ?? {};
  const cover = isNonEmptyString(teamPictureUrl) ? MediaAttachmentFactory.fromUrl({ url: teamPictureUrl }) : null;

  return (
    <SectionBox
      id="team"
      title={t('agency:profile.team.title')}
      className={cx(className, 'sm:py-24 max-sm:py-16')}
      subtitle={
        <Trans
          i18nKey={'agency:profile.team.subtitle'}
          count={memberCount}
          values={{ count: memberCount, name: agencyName }}>
          <b>{'{{count}}'} members</b>
          in {'{{name}}'}'s team
        </Trans>
      }>
      <div className="lg:px-12 px-8">
        {cover && (
          <div className="lg:px-4 mb-8">
            <S.TeamPicture>
              <LazyImage
                strategy={'browser'}
                imgLoading={'lazy'}
                imgProps={{
                  srcSet: `${getGumletImg(cover, { preferredWidth: 800, mobile: true })} 480w,
                      ${getGumletImg(cover, { preferredWidth: 1024, mobile: false })} 800w`,
                  sizes: '(max-width: 600px) 480px, 800px',
                  src: getGumletImg(cover, { preferredWidth: 800, mobile: true }),
                  alt: `${agencyName} cover`,
                  onError: onImageError,
                  className: 'width-100 rounded-xl bg-secondary-50',
                }}
              />
            </S.TeamPicture>
          </div>
        )}
        {story && (
          <div className="layout-column px-4">
            <span className="mb-4 bold h6">{t('agency:profile.team.storyTitle')}</span>
            <ClampLines text={story} className="lh-2" />
          </div>
        )}
        <footer className="hide-xs px-8 mt-16 layout-row layout-align-center-center">
          <CtaBox
            title={t('agency:profile.team.cta.title', { name: agencyName })}
            subtitle={t('agency:profile.team.cta.subtitle')}
            btnLabel={t('agency:profile.team.cta.btnLabel')}
            icon={<ContactsTwoTone />}
            element="team-panel"
            agencySlug={agencySlug}
          />
        </footer>
      </div>
    </SectionBox>
  );
};
